import { WorkflowCreateRequestBodyV2 } from "@/types/WorkflowCreate/workflowCreate";
import { showWorkflowErrorNotification } from "../../helpers/formValidationNotification";

/**
 * Fully normalizes:
 * - workflowTasks.order
 * - transformationSteps.order + taskOrder
 * - fieldMappings source/destination task orders
 * - internal field mapping orders
 */
export const normalizeWorkflowRequestBody = (workflow: WorkflowCreateRequestBodyV2): WorkflowCreateRequestBodyV2 => {
  const clone: WorkflowCreateRequestBodyV2 = { ...workflow };
  // STEP 1 — Normalize workflowTasks order
  const sortedTasks = [...clone.workflowTasks].sort((a, b) => a.order - b.order);

  const taskOrderMap = new Map<number, number>(); // old → new order mapping

  clone.workflowTasks = sortedTasks.map((task, newIndex) => {
    const oldOrder = task.order;
    task.order = newIndex;
    taskOrderMap.set(oldOrder, newIndex); // build lookup
    return task;
  });

  // STEP 2 — Normalize transformationSteps (order + taskOrder)
  const sortedTransformSteps = [...clone.transformationSteps].sort(
    (a, b) => a.taskOrder - b.taskOrder
  );

  clone.transformationSteps = sortedTransformSteps
    .map((step, index) => {
      const oldTaskOrder = step.taskOrder;
      const newTaskOrder = taskOrderMap.get(oldTaskOrder);

      if (newTaskOrder === undefined) {
        const originalTaskIndex = taskOrderMap.get(oldTaskOrder);
        const stepNumber = index + 1;

        showWorkflowErrorNotification(
          originalTaskIndex !== undefined
            ? `Transformation Step ${stepNumber} references deleted task #${originalTaskIndex + 1}.`
            : `Transformation Step ${stepNumber} references a task that no longer exists.`
        );

        return null as unknown; // exclude invalid step during filtering
      }

      return {
        ...step,
        order: newTaskOrder,
        taskOrder: newTaskOrder,
      };
    })
    .filter((step): step is typeof sortedTransformSteps[number] => step !== null);

  // STEP 3 — Normalize fieldMappings
  clone.fieldMappings = clone.fieldMappings.map((fm) => {
    // Update task references
    if (typeof fm.sourceTaskOrder === "number") {
      fm.sourceTaskOrder =
        taskOrderMap.get(fm.sourceTaskOrder) ?? fm.sourceTaskOrder;
    }
    if (typeof fm.destinationTaskOrder === "number") {
      fm.destinationTaskOrder =
        taskOrderMap.get(fm.destinationTaskOrder) ?? fm.destinationTaskOrder;
    }

    // Normalize mapping field orders
    fm.mappings = [...fm.mappings]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((m, i) => ({ ...m, order: i }));

    return fm;
  });

  return clone;
}
